import { parseDBAddress } from 'app/api/client-jobs/client-jobs-parsers';
import { normalizeAddressId } from 'app/helpers/normalizeAddressId';
import { deleteJob, getJobFromStorage, saveJob } from 'app/ui/modules/job-create/helpers/storageHelpers';
import axios from 'axios';
import { pick, sortBy, uniq } from 'lodash-es';
import moment from 'moment';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppNS } from '../';
import {
  apiBookLead,
  apiGetCheckoutState,
  apiGetJobById,
  apiGetNewJobResults,
  apiGetPermitsByAddress,
  apiGetPermitsById,
  apiGetProjectLeadById,
  apiSendNewJob,
} from '../api/client-jobs/client-jobs';
import { AppThunk } from './index';

export interface ClientJobsState {
  clientJobs: Array<AppNS.Project>;
  currentLead: AppNS.ProjectLead | null;
  isCurrentLeadValid: boolean;
  currentLeadValidErrors: Array<string>;
  isCurrentLeadValidChecked: boolean;
  isCurrentLeadSending: boolean;
  isCurrentLeadCheckoutInProgress: boolean;
  currentLeadCheckoutId: string;
  currentJob: AppNS.Project | null;
  newJob: AppNS.Project;
  isNewJobValid: boolean;
  isNewJobValidChecked: boolean;
  newJobValidErrors: Array<string>;
  isNewJobLoading: boolean;
  isNewJobError: AppNS.Params | null;
  isJobResultLoaded: boolean;
  newJobResults: Array<AppNS.MatchingResult>;
  jobSubmitted: string;
  isNewJobLoadingError: boolean;
  isPermitsLoadingError: boolean;
  isPermitsLoadingTimeout: boolean;
  isPermitsLoading: boolean;
  isPermitsLoaded: boolean;
  permitsValidErrors: Array<string>;
  foundPermitsAddressesMatched: Array<string>;
  searchPermitsItemsNotFound: Array<string>;
  isPermitsValid: boolean;
  isPermitValidChecked: boolean;
  isPermitDetailsLoadingError: boolean;
  isPermitDetailsLoading: string;
  isTermsAccepted: boolean;
  bookingInvoiceData: string;
  isBookConfirmError: string;
  paymentMethodNotRequired: boolean;
}

const emptyJob = {
  id: '',
  address: null,
  dbAddress: null,
  permits: [],
  pickedPermits: [],
  owner: '',
  projectType: '',
  description: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  startDate: '',
  endDate: '',
  searchBy: '',
  permitId: '',
  state: 'new',
  permitNumbers: [],
  inspectionTypes: [],
  addressIds: [],
  links: [{ url: '', name: '' }],
  type: 'regular',
  scope: '',
  paymentMethodNotRequired: false,
} as unknown as AppNS.Project;

const initialState: ClientJobsState = {
  clientJobs: [],
  newJob: getJobFromStorage() || emptyJob,
  currentJob: null,
  currentLead: null,
  isCurrentLeadValid: false,
  currentLeadValidErrors: [],
  isCurrentLeadValidChecked: false,
  isCurrentLeadSending: false,
  isCurrentLeadCheckoutInProgress: false,
  currentLeadCheckoutId: '',
  isNewJobValid: false,
  isNewJobValidChecked: false,
  newJobValidErrors: [],
  isNewJobLoading: false,
  isNewJobError: null,
  isJobResultLoaded: false,
  newJobResults: [] as Array<AppNS.MatchingResult>,
  jobSubmitted: '',
  isNewJobLoadingError: false,
  isPermitsLoadingError: false,
  isPermitsLoadingTimeout: false,
  isPermitsLoading: false,
  isPermitsLoaded: false,
  permitsValidErrors: [],
  foundPermitsAddressesMatched: [],
  searchPermitsItemsNotFound: [],
  isPermitsValid: false,
  isPermitValidChecked: false,
  isPermitDetailsLoadingError: false,
  isPermitDetailsLoading: '',
  isTermsAccepted: false,
  bookingInvoiceData: '',
  isBookConfirmError: '',
  paymentMethodNotRequired: false,
};

function checkPermitInspections(permits: Array<AppNS.PickedPermit>): boolean {
  let result = true;
  permits.forEach((permit) => {
    if (permit.isOn && permit.inspections?.length === 0) {
      result = false;
    }
  });
  return result;
}

function checkPermits(permits: Array<AppNS.PickedPermit>): boolean {
  let result = false;
  permits.forEach((permit) => {
    if (permit.isOn) {
      result = true;
    }
  });
  return result;
}

function ifPickedPermitsValid(state: ClientJobsState) {
  // if there's no certifications show empty form
  const job = state.newJob;

  const validationErrors = [];

  if (!state.isTermsAccepted) {
    validationErrors.push('Project terms must be accepted.');
  }

  if (!checkPermits(job.pickedPermits)) {
    validationErrors.push('Please pick some permits');
  }

  if (!checkPermitInspections(job.pickedPermits)) {
    validationErrors.push('There cannot be checked permit without picked inspection. Please pick some inspection.');
  }

  state.permitsValidErrors = validationErrors;
  state.isPermitsValid = validationErrors.length ? false : true;
}

function checkLeadValid(state: ClientJobsState) {
  const lead = state.currentLead;
  if (!lead) {
    state.isCurrentLeadValid = false;
    return;
  }

  const validationErrors = [];

  console.log(lead.paymentMethodId);

  if (!lead.paymentMethodId && !state.paymentMethodNotRequired) {
    validationErrors.push('Please pick a valid credit card');
  }
  if (lead.proposal?.paymentArrangement === 'bulk' && !state.bookingInvoiceData) {
    validationErrors.push('Please fill invoice details for this booking');
  }

  state.currentLeadValidErrors = validationErrors;
  state.isCurrentLeadValid = validationErrors.length > 0 ? false : true;
}

function checkNotFoundPermits(permits: Array<AppNS.Permit>, request: string): Array<string> {
  const requestArray = request.split(',');
  return requestArray.filter((requestItem: string) => {
    const trimmed = requestItem.trim();
    return (
      permits.filter((permit: AppNS.Permit) => permit.permitNumber.toLowerCase() === trimmed.toLowerCase()).length === 0
    );
  });
}

const clientJobs = createSlice({
  name: 'clientJobs',
  initialState,
  reducers: {
    setNewJobAddress(state: ClientJobsState, action: PayloadAction<AppNS.ProjectAddress | null>) {
      state.newJob.address = action.payload;
      saveJob(state.newJob);
    },
    setNewJobPermitId(state: ClientJobsState, action: PayloadAction<string>) {
      state.newJob.permitId = action.payload;
      saveJob(state.newJob);
    },
    setSearchByType(state: ClientJobsState, action: PayloadAction<AppNS.ProjectSearchByType>) {
      state.newJob.searchBy = action.payload;
      if (action.payload === 'permit-special') {
        state.newJob.type = 'special';
      } else if (action.payload === 'permit-doee') {
        state.newJob.type = 'doee';
      } else {
        state.newJob.type = 'regular';
      }
      saveJob(state.newJob);
    },
    setNewJobPermits(state: ClientJobsState, action: PayloadAction<Array<AppNS.Permit>>) {
      state.newJob.permits = action.payload;
    },
    setPaymentMethodId(state: ClientJobsState, action: PayloadAction<string>) {
      if (state.currentLead) {
        state.currentLead.paymentMethodId = action.payload;
      }
      checkLeadValid(state);
    },
    setBookingInvoiceData(state: ClientJobsState, action: PayloadAction<string>) {
      if (state.currentLead) {
        state.bookingInvoiceData = action.payload;
      }
      checkLeadValid(state);
    },
    setPaymentMethodNotRequired(state: ClientJobsState, action: PayloadAction<boolean>) {
      state.paymentMethodNotRequired = action.payload;
    },
    setCurrentLeadValidChecked(state: ClientJobsState, action: PayloadAction<boolean>) {
      checkLeadValid(state);
      state.isCurrentLeadValidChecked = true;
    },
    setRequirements(state: ClientJobsState, action: PayloadAction<AppNS.Params>) {
      state.newJob = { ...state.newJob, ...action.payload };
    },
    setIsNewJobValidChecked(state: ClientJobsState) {
      state.isNewJobValidChecked = true;
    },
    sendNewJobStart(state: ClientJobsState) {
      state.isNewJobError = null;
      state.isNewJobLoading = true;
    },
    sendNewJobSuccess(state: ClientJobsState, action: PayloadAction<string>) {
      state.isNewJobLoading = false;
      state.newJob.id = action.payload;

      // save to localStorage with new id
      // saveJob(state.newJob);
    },
    sendNewJobFailed(state: ClientJobsState, action: PayloadAction<AppNS.Params>) {
      state.isNewJobLoading = false;
      state.isNewJobError = action.payload;
    },
    setSubmitState(state: ClientJobsState, action: PayloadAction<string>) {
      state.jobSubmitted = action.payload;
    },
    getNewJobResultsSuccess(state: ClientJobsState, action: PayloadAction<Array<AppNS.MatchingResult>>) {
      state.isJobResultLoaded = true;
      state.newJobResults = action.payload;
    },
    getActionFailed(state: ClientJobsState) {
      state.isNewJobLoading = false;
    },
    getJobResultsFailed(state: ClientJobsState) {
      // stop polling
      state.isJobResultLoaded = true;
    },
    resetResults(state: ClientJobsState) {
      state.isJobResultLoaded = false;
      state.newJobResults = [] as Array<AppNS.MatchingResult>;
    },
    resetJob(state: ClientJobsState) {
      state.newJob = emptyJob;
      state.isTermsAccepted = false;
      deleteJob();
    },
    resetPermitsValidation(state: ClientJobsState) {
      state.permitsValidErrors = [];
      state.isPermitsValid = false;
      state.isPermitValidChecked = false;
    },
    getJobStart(state: ClientJobsState) {
      state.isNewJobLoadingError = false;
      state.isNewJobLoading = true;
    },
    getJobFailed(state: ClientJobsState) {
      state.isNewJobLoading = false;
      state.isNewJobLoadingError = true;
    },
    getJobSuccess(state: ClientJobsState, action: PayloadAction<AppNS.Project>) {
      state.isNewJobLoadingError = false;
      state.isNewJobLoading = false;
      state.currentJob = action.payload;
    },
    setCurrentJobSwoState(state: ClientJobsState, action: PayloadAction<AppNS.Project>) {
      if (state.currentJob?.id === action.payload.id) {
        state.currentJob.swoSetAt = action.payload.swoSetAt;
      }
    },
    getCurrentLeadSuccess(state: ClientJobsState, action: PayloadAction<AppNS.ProjectLead>) {
      state.isNewJobLoadingError = false;
      state.isNewJobLoading = false;
      state.isCurrentLeadValid = false;
      state.currentLead = action.payload;
    },
    bookLeadReset(state: ClientJobsState) {
      state.isBookConfirmError = '';
      state.isCurrentLeadSending = false;
      state.isCurrentLeadCheckoutInProgress = false;
      state.currentLeadCheckoutId = '';
      state.currentLead && state.currentLead.job && (state.currentLead.job.state = 'new');
    },
    bookLeadStart(state: ClientJobsState) {
      state.isBookConfirmError = '';
      state.isCurrentLeadSending = true;
    },
    bookLeadFailed(state: ClientJobsState, action: PayloadAction<string>) {
      state.isCurrentLeadSending = false;
      state.isBookConfirmError = action.payload;
    },
    bookLeadSuccess(state: ClientJobsState, action: PayloadAction<string>) {
      state.isCurrentLeadSending = false;

      if (state.currentLead && state.currentLead.job) {
        state.currentLeadCheckoutId = action.payload;
        state.currentLead.job.state = 'booking_in_progress';
        state.isBookConfirmError = '';
      }
    },
    checkoutLeadStart(state: ClientJobsState) {
      state.isBookConfirmError = '';
      state.isCurrentLeadCheckoutInProgress = true;
    },
    checkoutLeadFailed(state: ClientJobsState, action: PayloadAction<string>) {
      state.isCurrentLeadCheckoutInProgress = false;
      state.isBookConfirmError = action.payload;
    },
    checkoutLeadSuccess(state: ClientJobsState, action: PayloadAction<AppNS.Params>) {
      state.isCurrentLeadCheckoutInProgress = false;

      if (action.payload.state === 'failed') {
        state.isBookConfirmError = action.payload.errorMessage || 'Unexpected Error. Please try again.';
      } else {
        if (state.currentLead && state.currentLead.job) {
          if (action.payload.state === 'completed') {
            state.currentLead.job.state = 'confirmed_booking';
            state.isBookConfirmError = '';
          }
        }
      }
    },
    getPermitsByAddressStart(state: ClientJobsState) {
      state.searchPermitsItemsNotFound = [];
      state.foundPermitsAddressesMatched = [];
      state.isPermitsLoadingTimeout = false;
      state.isPermitsLoadingError = false;
      state.isPermitsLoading = true;
      state.isPermitsLoaded = false;
    },
    getPermitsByAddressFailed(state: ClientJobsState) {
      state.isPermitsLoading = false;
      state.isPermitsLoadingError = true;
    },
    getPermitsByAddressSuccess(
      state: ClientJobsState,
      action: PayloadAction<{ state: string; results: Array<AppNS.Permit> }>
    ) {
      if (action.payload.state === 'completed') {
        state.isPermitsLoadingError = false;
        state.isPermitsLoading = false;
        // stop polling
        state.isPermitsLoaded = true;
        state.newJob.permits = sortBy(action.payload.results, (res) =>
          moment(res.recStatusDate, 'MM/DD/YYYY')
        ).reverse();

        // save all possible unique address strings from permits for this address
        const addressIds = action.payload.results.map((item: AppNS.Permit) => item.addressId);
        state.newJob.addressIds = uniq(addressIds) as Array<string>;
        ifPickedPermitsValid(state);
      } else if (action.payload.state === 'timeout') {
        state.isPermitsLoadingTimeout = true;
        state.isPermitsLoading = false;
        // stop polling
        state.isPermitsLoaded = true;
      }
    },
    getPermitByIdStart(state: ClientJobsState) {
      state.foundPermitsAddressesMatched = [];
      state.searchPermitsItemsNotFound = [];
      state.isPermitsLoadingError = false;
      state.isPermitsLoadingTimeout = false;
      state.isPermitsLoading = true;
      state.newJob.dbAddress = null;
    },
    getPermitByIdFailed(state: ClientJobsState) {
      state.isPermitsLoading = false;
      state.isPermitsLoadingError = true;
    },
    getPermitByIdSuccess(state: ClientJobsState, action: PayloadAction<Array<AppNS.Permit>>) {
      state.isPermitsLoadingError = false;
      state.isPermitsLoadingTimeout = false;
      state.isPermitsLoading = false;
      state.isJobResultLoaded = true;
      state.newJob.permits = action.payload;
      if (action.payload.length > 0) {
        const addressIds = action.payload.map((item: AppNS.Permit) => normalizeAddressId(item.addressId));
        state.newJob.addressIds = uniq(addressIds);
        state.foundPermitsAddressesMatched = state.newJob.addressIds;
        if (state.foundPermitsAddressesMatched.length > 1) {
          state.newJob.dbAddress = null;
        } else {
          state.newJob.dbAddress = parseDBAddress(action.payload[0].addressInfo);
        }
        state.searchPermitsItemsNotFound = checkNotFoundPermits(action.payload, state.newJob.permitId);
      }
      ifPickedPermitsValid(state);
      saveJob(state.newJob);
    },
    resetPickedPermits(state: ClientJobsState) {
      state.newJob.pickedPermits = [];
    },
    savePermitDetails(state: ClientJobsState, action: PayloadAction<AppNS.Permit>) {
      state.isPermitDetailsLoadingError = false;
      state.isPermitDetailsLoading = '';
      state.newJob.permits = state.newJob.permits.map((item) => {
        if (action.payload.id === item.id) {
          return { ...item, ...action.payload };
        }
        return item;
      });
      ifPickedPermitsValid(state);
      saveJob(state.newJob);
    },
    setPickedPermit(state: ClientJobsState, action: PayloadAction<AppNS.PickedPermit>) {
      let currentPickedPermits = state.newJob.pickedPermits;
      if (currentPickedPermits) {
        const result = currentPickedPermits.find((item) => item.id === action.payload.id);
        if (!result) {
          currentPickedPermits.push(action.payload);
        } else {
          currentPickedPermits = currentPickedPermits.map((item) => {
            if (item.id === action.payload.id) {
              return action.payload;
            }
            return item;
          });
        }
        state.newJob.pickedPermits = currentPickedPermits;
        saveJob(state.newJob);
        ifPickedPermitsValid(state);
      }
    },
    setIsPermitValidChecked(state: ClientJobsState) {
      state.isPermitValidChecked = true;
    },
    setTerms(state: ClientJobsState, action: PayloadAction<boolean>) {
      state.isTermsAccepted = action.payload;
      ifPickedPermitsValid(state);
    },
    checkNewJob(state: ClientJobsState) {
      ifPickedPermitsValid(state);
    },
    setAdditionalInfo(
      state: ClientJobsState,
      action: PayloadAction<
        Pick<
          AppNS.Project,
          'projectType' | 'startDate' | 'endDate' | 'description' | 'contactName' | 'contactEmail' | 'contactPhone'
        >
      >
    ) {
      state.newJob = {
        ...state.newJob,
        ...action.payload,
      };

      saveJob(state.newJob);
      ifPickedPermitsValid(state);
    },
    setDoeeDetails(
      state: ClientJobsState,
      action: PayloadAction<
        Pick<
          AppNS.Project,
          | 'projectOwnerName'
          | 'startDate'
          | 'endDate'
          | 'description'
          | 'contactName'
          | 'contactEmail'
          | 'contactPhone'
          | 'scopes'
          | 'jobDocuments'
        >
      >
    ) {
      state.newJob = {
        ...state.newJob,
        ...pick(action.payload, [
          'projectOwnerName',
          'startDate',
          'endDate',
          'description',
          'contactName',
          'contactEmail',
          'contactPhone',
          'scopes',
          'jobDocuments',
        ]),
      };

      saveJob(state.newJob);
      ifPickedPermitsValid(state);
    },
  },
});

export const {
  bookLeadFailed,
  bookLeadStart,
  bookLeadSuccess,
  bookLeadReset,
  checkNewJob,
  getActionFailed,
  getCurrentLeadSuccess,
  getJobFailed,
  getJobResultsFailed,
  getJobStart,
  getJobSuccess,
  getNewJobResultsSuccess,
  getPermitsByAddressFailed,
  getPermitsByAddressStart,
  getPermitsByAddressSuccess,
  getPermitByIdFailed,
  getPermitByIdStart,
  getPermitByIdSuccess,
  savePermitDetails,
  resetJob,
  resetResults,
  resetPickedPermits,
  sendNewJobStart,
  sendNewJobSuccess,
  sendNewJobFailed,
  setAdditionalInfo,
  setPaymentMethodId,
  setCurrentLeadValidChecked,
  setPaymentMethodNotRequired,
  setIsNewJobValidChecked,
  setIsPermitValidChecked,
  setNewJobAddress,
  setNewJobPermitId,
  setNewJobPermits,
  setPickedPermit,
  setTerms,
  setRequirements,
  setSubmitState,
  setSearchByType,
  resetPermitsValidation,
  setBookingInvoiceData,
  checkoutLeadStart,
  checkoutLeadSuccess,
  checkoutLeadFailed,
  setDoeeDetails,
  setCurrentJobSwoState,
} = clientJobs.actions;

export default clientJobs.reducer;

export const sendJob =
  (job: AppNS.Project): AppThunk =>
  async (dispatch) => {
    try {
      console.log('sendJob', job);

      dispatch(sendNewJobStart());
      const response = await apiSendNewJob(job);
      dispatch(sendNewJobSuccess(response));
      dispatch(setSubmitState(response));
    } catch (err: any) {
      if (err) {
        dispatch(sendNewJobFailed(err));
      }
    }
  };

export const getJob =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      let job: AppNS.Project | null;
      dispatch(getJobStart());

      job = await apiGetJobById(id);
      dispatch(getJobSuccess(job));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getJobFailed());
    }
  };

export const getCurrentLead =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getJobStart());
      const result = await apiGetProjectLeadById(id);
      dispatch(getCurrentLeadSuccess(result));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getJobFailed());
    }
  };

export const fetchJobResults =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      const response = await apiGetNewJobResults(id);
      dispatch(getNewJobResultsSuccess(response));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(getJobResultsFailed());
    }
  };

export const bookLead =
  (lead: AppNS.ProjectLead, clientInvoiceData: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(bookLeadStart());
      const response = await apiBookLead(lead, clientInvoiceData);
      dispatch(bookLeadSuccess(response));
    } catch (err: any) {
      if (err) {
        dispatch(bookLeadFailed(err as string));
      }
    }
  };

export const getCheckoutState =
  (checkoutId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(checkoutLeadStart());
      const response = await apiGetCheckoutState(checkoutId);
      dispatch(checkoutLeadSuccess(response));
    } catch (err: any) {
      if (err) {
        dispatch(checkoutLeadFailed(err as string));
      }
    }
  };

export const getPermitsByAddress =
  (address: AppNS.ProjectAddress): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getPermitsByAddressStart());
      const result = await apiGetPermitsByAddress(address);

      dispatch(
        getPermitsByAddressSuccess({
          results: result.results,
          state: result.state,
        })
      );
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(getPermitsByAddressFailed());
    }
  };

export const getPermitsById =
  (ids: string, jobType: AppNS.ProjectType): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getPermitByIdStart());
      const idsArray = ids.split(',').map((id: string) => {
        return id.trim().toLowerCase();
      });

      const permits = await apiGetPermitsById(uniq(idsArray), jobType);

      dispatch(getPermitByIdSuccess(permits));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err);
      dispatch(getPermitByIdFailed());
    }
  };
